'use client';

import { useRestaurantSite } from '@/src/lib/hospitality/useRestaurantSite';
import { BlockSection, SiteStateNotice, useHospitalityMessages } from './shared';

/**
 * The restaurant's name, where it is, and the way in.
 *
 * <p>Everything factual comes from `RestaurantSiteDto` through the host data
 * source — the name and the address are the operator's `Channel` and
 * `Location`, not props, so a tenant that renames itself does not have to
 * re-edit a page. The props are the editorial half only: a strapline the
 * operator writes and up to two calls to action pointing at pages they have
 * authored.
 *
 * <p>No header, no navigation, no `<main>`. The Shell owns all three.
 */
export interface RestaurantHeroProps {
  /** Operator's own line under the name. Optional — no invented copy. */
  strapline?: string;
  primaryCtaLabel?: string;
  primaryCtaHref?: string;
  secondaryCtaLabel?: string;
  secondaryCtaHref?: string;
  /** `h1` on a home page, `h2` when the block sits under one. Defaults to `h1`
   *  because this is normally the page's leading block. */
  headingLevel?: 'h1' | 'h2';
}

export function RestaurantHero({
  strapline,
  primaryCtaLabel,
  primaryCtaHref,
  secondaryCtaLabel,
  secondaryCtaHref,
  headingLevel = 'h1',
}: RestaurantHeroProps) {
  const t = useHospitalityMessages();
  const state = useRestaurantSite();
  const notice = <SiteStateNotice state={state} t={t} />;
  if (state.status !== 'ready') {
    return <BlockSection>{notice}</BlockSection>;
  }

  const { site } = state;
  const Heading = headingLevel;
  const address = site.addressText ?? site.locationName;

  return (
    <BlockSection>
      <Heading className="text-4xl font-semibold tracking-tight">{site.name}</Heading>
      {strapline ? (
        <p className="mt-3 max-w-2xl text-lg text-[var(--color-text-secondary)]">{strapline}</p>
      ) : null}
      {address ? (
        <address className="mt-4 not-italic text-[var(--color-text-secondary)]">
          <span className="sr-only">{t('hospitality.hero.addressLabel')} </span>
          {address}
        </address>
      ) : null}
      {primaryCtaHref && primaryCtaLabel ? (
        <p className="mt-6 flex flex-wrap gap-3">
          <a
            href={primaryCtaHref}
            className="rounded-md bg-[var(--color-brand-500)] px-4 py-2 text-sm font-medium text-[var(--color-text-on-brand)]"
          >
            {primaryCtaLabel}
          </a>
          {secondaryCtaHref && secondaryCtaLabel ? (
            <a
              href={secondaryCtaHref}
              className="rounded-md border border-[var(--color-border)] px-4 py-2 text-sm font-medium"
            >
              {secondaryCtaLabel}
            </a>
          ) : null}
        </p>
      ) : null}
    </BlockSection>
  );
}
